// TypeScript schemas mirroring the legacy FastAPI /api/admin/* contracts.
// All positions are metres in the admin Y-up coordinate frame: [x, y, z].
//
// Single source of truth: web's `web/src/lib/adminTypes.ts` used to be its
// own hand-maintained ~600-line copy of this file, and the two drifted apart
// more than once (a field added here would silently not appear there, or
// vice versa — TypeScript's structural typing doesn't flag a missing
// optional field). web/src/lib/adminTypes.ts is now a thin `export *` of
// this file; add every admin wire-shape type HERE, not there.

// Type-only, so this doesn't create a runtime cycle even though
// editorState.ts imports admin wire types from this file too — see the
// onboarding template types near the bottom of this file for why.
import type { EditorDoc, PanelEntry } from './editorState';
import type { PersistedVizPayload } from './persistedViz';
import { PLACEMENT_GALLERY_LIMIT } from './placementConstants';

export type Vec3 = [number, number, number];

export type SpeakerSubkind =
  | 'subwoofer' | 'nearfield' | 'bookshelf' | 'floorstanding' | 'overhead';
export type SpeakerRole = 'main_l' | 'main_r' | null;
export type Furnishing = 'empty' | 'light' | 'moderate' | 'heavy';
export type ModalPipeline = 'auto' | 'voxel' | 'analytical';
export type SurfaceKey = 'floor' | 'front' | 'back' | 'left' | 'right';
export type TreatmentTier = 'low' | 'medium' | 'high';
/** How many panels the treatment solve places — minimum=6 / medium=10 /
 *  maximum=16, or 'custom' for a caller-chosen total (see `custom_count`
 *  on `JobAction`'s `place_treatment` variant, 1-40).  Orthogonal to
 *  `TreatmentTier` (which sets the cost caps); maps to the backend
 *  greedy's `max_products` total budget. */
export type Coverage = 'minimum' | 'medium' | 'maximum' | 'custom';

/** One SKU's worth of a caller-chosen product mix — see `manual_selection`
 *  on `JobAction`'s `place_treatment` variant. `quantity` is 1-20 per SKU
 *  (backend-enforced); wins over `coverage`/`custom_count` on the wire when
 *  both are sent. */
export interface ManualSelectionItem {
  sku: string;
  quantity: number;
}

/** Standard audio layouts the position-search backend understands.
 *  Each value maps to a fixed channel-angle table in admin_optimize.py
 *  (LAYOUT_ANGLES).  Layout drives the backend's "search_layout" path,
 *  which generates ALL head-level speakers at one shared d_common
 *  radius from the listener at the layout's fixed channel angles. */
export type SpeakerLayout =
  | 'mono'
  | 'stereo' | 'stereo.2' | 'stereo.4'
  | '5.1' | '5.1.2' | '5.1.4'
  | '7.1' | '7.1.2' | '7.1.4';

export interface AdminSpeaker {
  position: Vec3;
  subkind: SpeakerSubkind | null;
  role?: SpeakerRole;
  freq_response?: [number, number][] | null; // [[hz, dB], ...]
}

export interface PanelSize { w: number; h: number; d: number; }

// Single source of truth for the mounting-type union — usable as a TYPE
// (ADMIN_PANEL_MOUNTING_TYPE below) and, because it's a real runtime array,
// as a VALUE tests can check membership against. Before this, AdminPanel and
// AdminProduct each hand-declared their own copy of this union, and they'd
// drifted apart: AdminProduct already had 'corner', AdminPanel didn't. That
// silent drift is what let a corner trap's mounting_type survive the wire
// and then have nowhere valid to land in PanelEntry.shape (see
// web/tests/unit/serialize.test.ts and contracts/panel-wire-examples.json).
// One declaration can't drift from itself.
export const ADMIN_PANEL_MOUNTING_TYPES = ['wall', 'ceiling', 'floor', 'corner'] as const;
export type AdminPanelMountingType = typeof ADMIN_PANEL_MOUNTING_TYPES[number];

export interface AdminPanel {
  label?: string | null;
  sku?: string | null;
  subkind?: string | null;
  size: PanelSize;
  position: Vec3;
  rotation: Vec3; // radians, Y-up
  mounting_type?: AdminPanelMountingType;
  /** The panel's plan-view footprint: panel-local `[x, z]` metres, wound CCW,
   *  which the backend extrudes by `size.h` to get the real absorbing prism
   *  instead of a bounding box. Absent (the common case) means "a plain
   *  centred `size.w × size.d` rectangle" — so a flat panel's payload is
   *  unchanged and the solver takes its pre-existing box path.
   *
   *  Produced by @roomtreatment/panel-surface's `panelFootprint`; the
   *  convention and the golden cases live in
   *  contracts/panel-footprint-examples.json, which the backend's
   *  `footprint_from_panel_dict` is tested against too. */
  footprint?: number[][] | null;
  /** Air gap (standoff) behind the panel, metres. Backend translates the panel
   *  off its wall along the front normal by this much, opening an LF cavity. */
  airgap_m?: number | null;
  /** Explicit flow resistivity (rayl/m); overrides the thickness back-fit. */
  sigma?: number | null;
}

/** A placed furniture item on the wire.  The backend rasterizes the
 *  position/rotation/size OBB to SOLID voxels (air displacement) and uses it
 *  as a broadband ray absorber; `material` selects the furniture α curve and
 *  `kind` distinguishes solid blocks from sub-pitch floor patches (rugs). */
export interface AdminFurniture {
  preset_id: string;
  size: PanelSize;
  position: Vec3;   // [x,y,z] metres, Y-up (admin frame)
  rotation: Vec3;   // [rx,ry,rz] radians, XYZ Euler, Y-up
  /** Absorption-material key, or null for `kind: 'decor'`, which reaches no
   *  absorption path. Backend: `_AdminFurniture.material: Optional[str]`. */
  material: string | null;
  kind: 'solid' | 'floorPatch' | 'decor';
  /** Is this a seat (sofa, armchair, dining/bar chair, …)? Set by
   *  `furnitureToWire` from the preset's `category === 'Seating'`
   *  (`packages/room-flow/src/furniturePresets.ts`) — the client owns what
   *  counts as a seat, the same split `kind` already follows for what to do
   *  with the item physically. Backend: `_AdminFurniture.seat: bool = False`.
   *  Drives seat-anchored placement search (R5) — see
   *  docs/plans/2026-09-02-unified-room-solve-plan.md §4.5. */
  seat: boolean;
}

/** Glazed opening on a host wall/ceiling. Polygon is host-frame (u, v) metres.
 *  TODO(glass-curve): `material` defaults to glass_heavy; thinner/IGU later. */
export interface AdminWindow {
  surface: string;
  polygon: number[][];
  u?: number;
  v?: number;
  w?: number;
  h?: number;
  thickness?: number;
  material?: string;
}

export interface MaterialAssignmentWire {
  target_kind: 'floor' | 'ceiling' | 'wall' | 'furniture';
  target_id: string;
  material_id: string;
}

export interface RoomDims { Lx: number; Ly: number; H: number; }


// --- room geometry (footprint polygon, wings, ceiling profile) -----------
export type RoomShape = 'rectangular' | 'l_shape';

/** A floor-polygon corner in metres. x = depth (front→back), y = width. */
export interface Corner { x: number; y: number; label?: string }

/** An L-room wing extension on one wall. */
export interface WingSpec {
  wall: 'front' | 'back' | 'left' | 'right';
  depth: number;
  width: number;
  offset: number;
}

/** One [t, z] ceiling node: t in [0,1] along the axis, z in metres. */
export type CeilingNode = [number, number];
export interface AxisProfile { nodes: CeilingNode[]; mode: 'linear' | 'smooth' }
/** Separable 2D ceiling profile — the shape backend `ceiling_profile_from_dict` accepts. */
export interface CeilingProfile {
  x: AxisProfile | null;
  y: AxisProfile | null;
  primary_axis: 'x' | 'y';
}

export interface SceneBase extends RoomDims {
  speakers: AdminSpeaker[];
  listener_pos: Vec3;
  panels?: AdminPanel[];
  furniture?: AdminFurniture[];
  windows?: AdminWindow[];
  floor_material?: string | null;
  wall_material?: string | null;
  materials?: {
    assignments?: MaterialAssignmentWire[];
    defaults?: { floor?: string; ceiling?: string; wall?: string; furniture?: string };
    definitions?: unknown[];
  };
  // Optional complex geometry — absent ⇒ a plain rectangular room.  When
  // present the backend voxel pipeline solves on the real room shape.
  room_shape?: RoomShape;
  footprint_vertices?: number[][] | null;
  wall_labels?: (string | null)[] | null;
  wings?: WingSpec[];
  ceiling_profile?: CeilingProfile | null;
}

// --- freq_response -------------------------------------------------------
export interface FreqResponseRequest extends SceneBase {
  fmax?: number;
}
/** One speaker's own complex contribution to the combined `mag`/`phase` —
 *  summing every entry's mag/phase (as complex numbers) reconstructs the
 *  combined response exactly (verified backend-side, see
 *  test_freq_response_per_speaker.py). See Per-Speaker FR/Phase charts. */
export interface PerSpeakerFreqResponse {
  index: number;
  subkind: string | null;
  mag: number[];
  phase: number[];
}

/** One SBIR comb-filter null predicted from image-source reflection
 *  geometry (see backend/sbir_attribution.py). A geometric *prediction*,
 *  not a measurement off the modal solve — consumers must label it as
 *  such. Not currently rendered on any chart (see AGENTS.md/git history for
 *  why the FR chart's predicted-null ticks were removed). `facet_id` is
 *  a raw WallGrid label (`left`/`back`/`right`/`front`/`ceiling`/`floor`,
 *  plus `wing_right`/`junction` for L-shaped rooms), not a semantic name. */
export interface PredictedNull {
  freq_hz: number;
  facet_id: string;
  order: number;
  path_diff_m: number;
}

export interface FreqResponseResponse {
  freqs: number[];
  mag: number[];
  mag_baseline?: number[];
  /** Radians, wrapped to (-pi, pi] (np.angle). Same complex transfer
   *  function `mag` is the magnitude of — see Phase/Group Delay charts. */
  phase?: number[];
  phase_baseline?: number[];
  per_speaker?: PerSpeakerFreqResponse[];
  predicted_nulls?: PredictedNull[];
}

// --- rt60 ----------------------------------------------------------------
export interface RT60Request extends SceneBase {
  n_rays?: number;
  n_bounces?: number;
  fmax?: number;
  furnishing?: Furnishing;
}
/** Fine-grid modal RT60 — parallel frequency/value arrays, 20-200 Hz. */
export interface RT60Fine { freqs: number[]; rt60: number[]; }

/** Per-band, per-time-bin Schroeder energy decay curve (dB, 0 at t=0,
 *  decreasing) — the ray-traced pipeline's raw data before it's fit down to
 *  a single RT60 number. ≥200 Hz only (the <200 Hz modal region has no
 *  time-domain curve). See Waterfall/Spectrogram/Clarity/Decay-Fit charts. */
export interface RT60DecayCurve {
  bandHz: number[];
  timeS: number[];
  edcDb: number[][];
}

export interface RT60Response {
  rt60: Record<string, number>;            // per octave band, ≥200 Hz
  rt60_fine: RT60Fine;                      // fine grid 20-200 Hz
  rt60_baseline?: Record<string, number>;
  rt60_fine_baseline?: RT60Fine;
  /** Echoed THIRD_OCTAVE band centres (Hz) `rt60`/`rt60_baseline` are keyed
   *  by — was always on the wire (backend/app.py's admin_rt60), just not
   *  previously declared here. */
  freqs_hz?: number[];
  /** Echoed `n_bounces` from the request — was always on the wire, just not
   *  previously declared here. */
  n_bounces?: number;
  n_arrivals?: number;
  n_rays?: number;
  decay?: RT60DecayCurve | null;
  decay_baseline?: RT60DecayCurve | null;
  /** Per-mode resonant frequency + Q, <200 Hz — feeds the Modal Ringing
   *  Fingerprint chart. Already computed internally to derive rt60_fine;
   *  this just serializes it. */
  modes?: { freq_hz: number; q: number }[];
  /** In-situ per-panel α(f). Present after a treated-room RT60; null/absent
   *  when the room has no panels. Compute reads this instead of a 4th POST. */
  panel_absorption?: PanelAbsorptionResponse | null;
}

// --- porous_absorber -------------------------------------------------------
/** Formula-based alpha(f) preview for a custom porous material — no room,
 *  no panel, no simulation. Mirrors backend/app.py's AdminPorousAbsorberRequest. */
export interface PorousAbsorberRequest {
  thickness_mm: number;
  flow_resistivity: number;   // Pa*s/m^2
  air_gap_mm?: number;
  random_incidence?: boolean;
}
export interface PorousAbsorberResponse {
  freq_hz: number[];
  alpha: number[];
  /** NRC: mean alpha at 250/500/1000/2000 Hz. */
  nrc: number;
}

// --- panel_absorption --------------------------------------------------------
/** Per-panel alpha(f) derived from a real modal simulation of the room +
 *  panel set — NOT a formula lookup. In-situ α: LF is absorbed/incident
 *  on the modal field; HF is method B energy-weighted α(θ). Not a share
 *  of Sabine A. See backend/panel_absorption.py. Mirrors
 *  backend/app.py's AdminPanelAbsorptionRequest. */
export interface PanelAbsorptionRequest extends SceneBase {
  fmax?: number;
  /** Match RT60 defaults (10000/60) so Compute's ray map is a cache hit.
   *  0 skips the HF ray half — modal-only, unsolved bands stay null. */
  n_rays?: number;
  n_bounces?: number;
}
export interface PanelAbsorptionCurve {
  /** Index into the request's `panels` list. Only guaranteed to match the
   *  request's position when every panel classifies successfully — see
   *  backend/app.py's AdminPanelAbsorptionCurve comment. */
  panel_index: number;
  /** `null` at a given band means no simulation produced a value there.
   *  LF holes (no modal energy in that window) stay null. HF above the
   *  last finite modal band is ray method B (α(θ)) when the ray map is
   *  available — never the material formula, never Sabine A/S. Truncate
   *  remaining nulls via @roomtreatment/room-flow's truncateSimulationCurve. */
  alpha: (number | null)[];
}
export interface PanelAbsorptionResponse {
  freq_hz: number[];
  panels: PanelAbsorptionCurve[];
}

// --- modal ---------------------------------------------------------------
export interface ModalRequest extends SceneBase {
  f: number;
  fmax?: number;
  surfaces?: SurfaceKey[] | null;
}
export interface ModalSurface {
  /** Nested: grid[u][v], NU rows of NV floats each — NOT a flat NU*NV
   *  array (backend's modal_analysis.evaluate_at_freq returns Pmag.tolist()
   *  on a (NU, NV) numpy array, which JSON-serializes as nested arrays). */
  grid: number[][];
  shape: [number, number]; // [NU, NV]
  vmax: number;
  u_min: number; u_max: number;
  v_min: number; v_max: number;
  u_axis: 'x' | 'y' | 'z';
  v_axis: 'x' | 'y' | 'z';
  fixed_axis: 'x' | 'y' | 'z';
  fixed_value: number;
  /** Extended 3-vector orientation, present on tilted facets (sloped
   *  ceiling segments, tilted walls).  When present the renderer should
   *  position the plane at `origin_vec` with the basis spanned by
   *  `u_axis_vec` × `v_axis_vec` — `fixed_axis/value` are a best-effort
   *  fallback for renderers that don't read the 3-vector form. */
  origin_vec?: [number, number, number];
  u_axis_vec?: [number, number, number];
  v_axis_vec?: [number, number, number];
  normal_vec?: [number, number, number];
}
export interface ModalResponse {
  f: number;
  modes_used: number;
  speakers_total: number;
  speakers_used: number;
  // Backend may emit wing-wall (`w0_far`, …) and ceiling-segment
  // (`ceiling_seg_N`) entries in addition to the 5 legacy keys, so the
  // surfaces dict is open-ended.  The renderer iterates `Object.entries`
  // and decides what to draw based on the entry's geometry, not the key.
  surfaces: Record<string, ModalSurface | undefined>;
}

/** `ModalSurface` with `grid` (NU x NV floats) stored as a content-
 *  addressed `ArtifactRef` (float32, shape [NU, NV]) instead of a nested
 *  `number[][]` — job pipeline W5, `modal_surface_refs`
 *  (`backend/routers/admin_physics.py`). `shape` and every other per-
 *  surface field stay inline. See `RaytraceRefResponse`'s header for why
 *  this exists; `resolveModal` (`@roomtreatment/api-client`) hydrates this
 *  back into the exact `ModalSurface` shape `ModalHeatmap.tsx` reads. */
export interface ModalSurfaceRef extends Omit<ModalSurface, 'grid'> {
  grid: ArtifactRef;
}

/** `ModalResponse` with every surface's `grid` replaced by a `ModalSurfaceRef`
 *  — what `analyze_scene` and placement-candidate `viz` now return instead
 *  of the inline nested-array form. The standalone `/api/admin/modal`
 *  endpoint and template precompute keep returning `ModalResponse`. */
export interface ModalRefResponse extends Omit<ModalResponse, 'surfaces'> {
  surfaces: Record<string, ModalSurfaceRef | undefined>;
}

// --- modal volume (3D pressure field) ------------------------------------
export interface ModalVolumeRequest extends SceneBase {
  f: number;
  fmax?: number;
  max_voxels?: number;
}
export interface ModalVolumeResponse {
  f: number;
  modes_used: number;
  speakers_used: number;
  grid: {
    origin: [number, number, number];
    h: [number, number, number];
    dims: [number, number, number];
    order: 'C';
  };
  field_b64: string;        // base64 float32, C-order nx*ny*nz
  inside_b64: string;       // base64 uint8, AIR mask
  vmax: number;
  warnings: string[];
}

// --- modal volume sweep (preloaded scrubbable cube) ---------------------
/** Sweep request — same scene as ModalVolumeRequest but with a frequency
 *  band instead of a single `f`.  The backend computes the whole cube in
 *  one matmul and returns it quantised so the client can scrub the slider
 *  in real time with zero further round trips. */
export interface ModalVolumeSweepRequest extends SceneBase {
  f_min?: number;          // Hz, default 20
  f_max?: number;          // Hz, default 300
  df?: number;             // Hz step, default 1
  fmax?: number;           // mode-enumeration ceiling for the eigensolve
  max_voxels?: number;
}

// --- modal particle-velocity field (band-cumulative) --------------------
/** Band-cumulative particle-velocity ("speed") field over the whole room.
 *
 *  Same scene and band as `ModalVolumeSweepRequest`, but the band is
 *  integrated away instead of scrubbed, so the response carries ONE field
 *  rather than a cube. `f_max` defaults to 200 Hz — greedy_solver's
 *  LATE_ENERGY_MODAL_CEILING_HZ — because above that modes overlap into a
 *  diffuse field and a per-mode velocity map stops meaning anything. */
export interface ModalVelocityRequest extends SceneBase {
  f_min?: number;          // Hz, default 20
  f_max?: number;          // Hz, default 200
  df?: number;             // Hz step, default 1
  fmax?: number;           // mode-enumeration ceiling for the eigensolve
  max_voxels?: number;
}

/** Shared metadata for the inline and artifact-referenced velocity response
 *  shapes — see `ModalVolumeSweepMeta`'s header for why this split exists. */
export interface ModalVelocityMeta {
  f_min: number;
  f_max: number;
  df: number;
  modes_used: number;
  speakers_used: number;
  grid: {
    origin: [number, number, number];
    h: [number, number, number];
    dims: [number, number, number];
    order: 'C';
  };
  vmax: number;
  warnings: string[];
}

export interface ModalVelocityResponse extends ModalVelocityMeta {
  /** base64 float32, C-order nx*ny*nz. Not quantised: a single grid is
   *  small enough that the sweep's per-slice uint8 packing would buy
   *  nothing but error. */
  field_b64: string;
  inside_b64: string;      // base64 uint8, AIR mask
}

/** `ModalVelocityResponse` with `field_b64`/`inside_b64` replaced by
 *  artifact references — see `VolumeSweepRefResponse`'s header. `field`'s
 *  `dtype` is `'float32'` here (the velocity field is not quantised),
 *  unlike the volume sweep's `'uint8'`. */
export interface VelocityRefResponse extends ModalVelocityMeta {
  field: ArtifactRef;
  inside: ArtifactRef;
}

/** Fields shared by the inline (`field_b64`) and artifact-referenced
 *  (`VolumeSweepRefResponse`) shapes — everything EXCEPT the bulk cube
 *  itself. Job pipeline W2 (2026-09-06): bulk fields move to content-
 *  addressed artifacts served from `GET /api/artifacts/<sha256>`; this
 *  split lets `sliceDecodedVolumetricSweep` and friends accept either shape
 *  without duplicating the metadata fields. */
export interface ModalVolumeSweepMeta {
  /** Echoed frequency axis in Hz, length F. */
  freqs: number[];
  modes_used: number;
  speakers_used: number;
  grid: {
    origin: [number, number, number];
    h: [number, number, number];
    dims: [number, number, number];
    order: 'C';
  };
  vmax_per_freq: number[];
  vmax_global: number;
  warnings: string[];
}

export interface ModalVolumeSweepResponse extends ModalVolumeSweepMeta {
  /** uint8 cube, C-order with frequency outermost: size F · nx · ny · nz.
   *  Each (nx · ny · nz) slice is normalised by `vmax_per_freq[f]` so the
   *  raw byte already maps 0..255 → 0..vmax_per_freq[f].  Multiply by
   *  (vmax_per_freq[f] / vmax_global) on the client to recover honest
   *  cross-frequency brightness without sacrificing per-slice precision. */
  field_b64: string;
  /** uint8 AIR mask over the voxel grid (nx · ny · nz). */
  inside_b64: string;
}

/** Content-addressed replacement for `field_b64`/`inside_b64` — the sha256
 *  of the (gzip-compressed on the wire) uncompressed bytes, fetched from
 *  `GET /api/artifacts/<sha256>` (see `fetchArtifact` in
 *  `@roomtreatment/api-client`). Same model as a share token: the hex id is
 *  the only credential. `dtype`/`shape` describe the UNCOMPRESSED bytes;
 *  `bytes` is their uncompressed length (for a pre-allocation / sanity
 *  check), and the transport is always gzip regardless of `Content-Encoding`
 *  (the artifact route never sets it — decompress client-side). */
export interface ArtifactRef {
  /** sha256 hex digest — also the artifact's id on `/api/artifacts/<sha>`. */
  artifact: string;
  /** Uncompressed byte length. */
  bytes: number;
  dtype: 'uint8' | 'float32';
  shape: number[];
  encoding: 'gzip';
}

/** Structural check for `ArtifactRef` — used to discriminate a ref-form
 *  wire shape (`RaytraceRefResponse`, `ModalSurfaceRef`, `VolumeSweepRefResponse`,
 *  `VelocityRefResponse`) from its inline counterpart without a runtime
 *  schema library. */
export function isArtifactRef(x: unknown): x is ArtifactRef {
  if (typeof x !== 'object' || x === null) return false;
  const rec = x as ArtifactRef;
  return typeof rec.artifact === 'string'
    && typeof rec.bytes === 'number'
    && Array.isArray(rec.shape)
    && (rec.dtype === 'uint8' || rec.dtype === 'float32')
    && rec.encoding === 'gzip';
}

/** `true` when `r` is the artifact-referenced form (`RaytraceRefResponse`)
 *  rather than the inline `RaytraceResponse` — checked on `positions`,
 *  which every raytrace response (empty or not) always carries. */
export function isRaytraceRef(
  r: RaytraceResponse | RaytraceRefResponse | null | undefined,
): r is RaytraceRefResponse {
  return r != null && isArtifactRef((r as RaytraceRefResponse).positions);
}

/** `true` when `m` is the artifact-referenced form (`ModalRefResponse`)
 *  rather than the inline `ModalResponse` — checked on the first surface's
 *  `grid`, since the ref/inline split happens per-surface on the wire. An
 *  empty `surfaces` map is reported as NOT a ref (nothing to resolve
 *  either way, and the inline shape is the more common empty case). */
export function isModalRef(
  m: ModalResponse | ModalRefResponse | null | undefined,
): m is ModalRefResponse {
  if (m == null) return false;
  for (const key in m.surfaces) {
    const surf = m.surfaces[key];
    if (surf) return isArtifactRef((surf as ModalSurfaceRef).grid);
  }
  return false;
}

/** `ModalVolumeSweepResponse` with `field_b64`/`inside_b64` replaced by
 *  artifact references — what `analyze_scene` (and any future queued
 *  caller) returns instead of the inline cube. */
export interface VolumeSweepRefResponse extends ModalVolumeSweepMeta {
  field: ArtifactRef;
  inside: ArtifactRef;
}

// --- speaker position sweep (Sweet Spot Map Mode B) ----------------------
/** Sweet Spot Map Mode B: fixed listener, sweep candidate SPEAKER
 *  positions on a grid. Mirrors `ModalVolumeSweepRequest`'s room shape;
 *  backend model is `AdminSpeakerSweepRequest` in backend/app.py. */
export interface SpeakerSweepRequest extends RoomDims {
  listener_pos: Vec3;
  fixed_speaker_positions?: Vec3[];
  sweep_axis_bounds: {
    x_min: number; x_max: number;
    z_min: number; z_max: number;
    grid_n?: number;
  };
  freq_lo_hz?: number;
  freq_hi_hz?: number;
  // Optional complex-geometry fields shared with SceneBase/_AdminGeometryFields.
  footprint_vertices?: number[][] | null;
  ceiling_profile?: CeilingProfile | null;
  wings?: WingSpec[];
  top_offsets?: number[][] | null;
  wall_labels?: (string | null)[] | null;
  furniture?: AdminFurniture[];
  windows?: AdminWindow[];
  materials?: unknown;
}

/** One candidate speaker-grid cell — `score` is lower = better bass
 *  consistency at the fixed listener (field-name-for-field mirror of
 *  backend `SpeakerSweepCell`). */
export interface SpeakerSweepCell {
  ix: number;
  iz: number;
  x: number;
  z: number;
  score: number;
}

/** Field-for-field mirror of backend `AdminSpeakerSweepResponse`. */
export interface AdminSpeakerSweepResponse {
  cells: SpeakerSweepCell[];
  nx: number;
  nz: number;
}

// --- modal warm ---------------------------------------------------------
// The separate /api/admin/modal_warm POST + GET endpoints are GONE.
// Modal warm is now a regular spawn-pool job (kind='modal_warm' in
// JobAction), submitted via /api/admin/jobs and polled via
// /api/admin/jobs/{id}.  The unification keeps the heavy MUMPS work
// out of the FastAPI main process where it used to starve the HTTP
// event loop and break polling at the BFF's 120s timeout.

// --- raytrace ------------------------------------------------------------
// Extends SceneBase so wings, custom polygons, and ceiling profiles all
// reach the backend tracer — the viz now bounces rays off the real room
// shape, not a bounding box.
export interface RaytraceRequest extends SceneBase {
  n_rays?: number;
  max_bounces?: number;
  alpha?: number;
}
export interface RaytraceResponse {
  n_points: number;
  positions: Vec3[];
  energies: number[];
  bounces: number[];
  /** Number of panels that actually projected onto a facet.  Panels whose
   *  orientation doesn't match any of the room's wall/ceiling-segment
   *  facets (rare — typically only odd hand-placed tilts) are dropped;
   *  this count lets the UI surface "X of Y panels applied". */
  n_panels_masked?: number;
  /** Path length in metres from source to this arrival (speaker -> ... ->
   *  this reflection point). Divide by the speed of sound for arrival time —
   *  see the Echogram chart. Always present on the wire (compute_raytrace
   *  has returned it since before this field was added to the type), just
   *  not previously declared here. */
  distances?: number[];
  /** Which speaker (index into the request's `speakers` array) this arrival
   *  originated from. */
  speaker_idx?: number[];
  /** Total rays traced (n_rays, evenly split across speakers) — always on
   *  the wire, just not previously declared here. */
  n_rays?: number;
  /** Echoed request listener position, admin Y-up — always on the wire,
   *  just not previously declared here. */
  listener_pos?: Vec3;
  /** Arrival-capture radius (metres) used for this trace — always on the
   *  wire, just not previously declared here. */
  listener_radius?: number;
  /** Echoed request room dimensions — always on the wire, just not
   *  previously declared here. */
  room?: { Lx: number; Ly: number; H: number };
}

/** `RaytraceResponse` with its five parallel arrays (`positions`/
 *  `energies`/`distances`/`bounces`/`speaker_idx`) stored as content-
 *  addressed `ArtifactRef`s instead of inline arrays — job pipeline W5,
 *  `raytrace_refs` (`backend/routers/admin_physics.py`). A 6000-ray inline
 *  result was 675 KB of JSON per Compute and per design save; only the
 *  wire form changes, never the physics. `positions` is float32 shape
 *  [N, 3]; `energies`/`distances` float32 shape [N]; `bounces`/
 *  `speaker_idx` uint8 shape [N]. Everything else (`n_points`, `room`,
 *  `listener_pos`, ...) stays inline. `analyze_scene` and placement-
 *  candidate `viz` return this form; the standalone `/api/admin/raytrace`
 *  endpoint and template precompute keep returning `RaytraceResponse`.
 *  `resolveRaytrace` (`@roomtreatment/api-client`) hydrates this back into
 *  the exact `RaytraceResponse` shape every 3D/chart consumer already
 *  reads (`RaytraceCloud.tsx`, `reflectionsFilter.ts`, `echogramDraw.ts`,
 *  ...) — no consumer should learn about this shape directly. */
export interface RaytraceRefResponse extends Omit<
  RaytraceResponse, 'positions' | 'energies' | 'distances' | 'bounces' | 'speaker_idx'
> {
  positions: ArtifactRef;
  energies: ArtifactRef;
  distances: ArtifactRef;
  bounces: ArtifactRef;
  speaker_idx: ArtifactRef;
}

// --- soundstage ----------------------------------------------------------
// Backed by `/api/admin/soundstage` (see `backend/soundstage_metrics.py`).
// Computed from a raytrace at the candidate's seat + chosen yaw.  All
// four metric fields are nullable: a missing input (e.g. no L/R mains for
// centre pull, or a hemisphere with zero arrivals) returns null rather
// than 0 so the UI surfaces "n/a" instead of pretending the value is
// meaningfully zero.
export interface SoundstageRequest extends SceneBase {
  listener_yaw_rad?: number;
  main_l_pos?: Vec3 | null;
  main_r_pos?: Vec3 | null;
  n_rays?: number;
  max_bounces?: number;
  alpha?: number;
}
export interface SoundstageResponse {
  centre_pull: {
    /** Direct-path interaural time difference (right − left, in ms).
     *  Sign convention: +ve → right speaker leads at the listener. */
    ictd_ms: number | null;
    /** Direct-path interaural level difference (right − left, in dB),
     *  from the inverse-square law on path lengths. */
    icld_db: number | null;
  };
  /** Right-vs-left early energy (0–80 ms) in dB.  +ve → right ear sees
   *  more early reflection energy. */
  lateral_balance_db: number | null;
  /** Percent difference between the two hemispheres' late-energy
   *  fractions (80–200 ms ÷ 0–200 ms).  0 = perfectly symmetric decay.
   *  Always non-negative — sign of which side is louder lives in
   *  `lef_l` vs `lef_r`. */
  decay_symmetry_pct: number | null;
  /** Per-hemisphere late-energy fraction in [0, 1].  Drives the
   *  per-side decay-tilt in the soundstage bar viz (the symmetric
   *  percent can't tell us which side is the slower-decaying one). */
  lef_l: number | null;
  lef_r: number | null;
  /** 1 − ρ of the 1 ms-binned L/R energy series over the early window
   *  — i.e. the decorrelation complement of early IACC, on the same
   *  scale.  Reported this way (not as IACC itself) so the metric is
   *  monotonic with perception: higher = wider, lower = narrower. */
  decorrelation_index: number | null;
  diagnostics: {
    n_arrivals_L: number;
    n_arrivals_R: number;
    energy_L_early: number;
    energy_R_early: number;
  };
  n_rays_total?: number;
  n_arrivals_total?: number;
}

// --- optimize ------------------------------------------------------------
export interface OptimizeCandidate {
  /** Solved seat — emitted by `solve_listener` (line + grid sweeps). */
  pos?: Vec3;
  /** Solved seat — emitted by `solve_joint`, which moves both seat and
   *  speakers.  Same coordinate as `pos`; the two solvers spell it
   *  differently so the union must accept either. */
  listener_pos?: Vec3;
  /** Listener facing (radians, yaw around Y) the solver chose for this
   *  candidate.  `search_layout` sweeps four cardinals (0, π/2, π, −π/2)
   *  and the winner per layout lives here so the frontend preview can
   *  rotate the listener marker to match.  Older response payloads
   *  predate this field — keep it optional and fall back to 0. */
  listener_yaw_rad?: number;
  speakers?: AdminSpeaker[];
  /** Subwoofer positions, in the order subs appear in the request's
   *  `speakers` array.  Populated by the modal-coupling sub search
   *  (`search_subs`) — applied to the doc's subwoofers by sub-index,
   *  independent of how `speakers` is matched to the layout channels. */
  subs?: { position: Vec3; subkind?: string | null }[];
  freq_response?: number[];
  score: number;
  /** Q-aware FR JND count.  Replaces the legacy weighted-RMS score; the
   *  `score` field above carries the cumulative (FR + soundstage) JND
   *  so existing sort logic keeps working without inversion. */
  fr_jnd?: number;
  /** Soundstage metrics + per-component JND breakdown.  Computed
   *  in-process inside the solver call (no more per-candidate HTTP
   *  fan-out from the frontend).  Null/absent on legacy responses
   *  and single-speaker scenes where soundstage is undefined. */
  soundstage?: SoundstageResponse | null;
  soundstage_jnd?: {
    ictd_jnd: number;
    icld_jnd: number;
    lateral_jnd: number;
    decay_jnd: number;
    decorr_jnd: number;
    total: number;
  } | null;
  soundstage_jnd_total?: number;
  /** FR JND + soundstage_jnd_total.  Lower wins. */
  cumulative_jnd?: number;
  /** Stage-0 geometric reflection-path symmetry JND (included in
   *  `cumulative_jnd`'s ranking) — was on the wire and computed since the
   *  Stage-0 pass landed, but never declared here, so typed code couldn't
   *  see it (the exact drift class AGENTS.md's "Admin wire types" note
   *  warns about — confirmed via `grep`, nothing outside this file
   *  referenced it before this declaration). */
  geom_sym_jnd?: number;
  /** The worst single reflection-pair path-length mismatch (metres) the
   *  Stage-0 hard cap was applied to. Same "computed, never declared"
   *  history as `geom_sym_jnd`. NOT attributable to one named wall — see
   *  `_reflection_set`/`compute_pair_reflection_asymmetry`
   *  (backend/geometric_symmetry.py): each speaker's reflection distances
   *  are sorted independently before being rank-compared, so the worst
   *  |Δd| can come from two different facets on the L and R sides. Report
   *  it as a distance, not as "closer to wall X". */
  max_pair_asym_m?: number;
  /** This candidate's worst frequency-response peak on the 1/6-octave-
   *  smoothed curve, selected by RADIATED-WEIGHTED audibility: a deviation
   *  the speakers barely reproduce loses to a smaller one they do. `db` is
   *  the true deviation at that bin, never a weighted number — the weight
   *  decides which bin is worst, not what it measures.
   *
   *  This used to be a hard `>= 30 Hz` search floor. It was removed
   *  because it produced the artefact it was meant to prevent: the
   *  speakers' rolloff rises monotonically out of the mask, so the search
   *  landed on the first in-range bin every time and the copy read
   *  "N dB at 30 Hz" for any speaker rolling off above it. Nothing is
   *  excluded by frequency now — see
   *  docs/plans/2026-08-25-fr-band-energy-weighting-plan.md.
   *
   *  `ring_s` is the mode's T60,
   *  `2.2·Q/f` — the same expression `admin_modal.py`'s
   *  `fr_derived_rt60_per_band` uses for the sub-200 Hz RT60 values the
   *  user is shown, so the two agree for one room. EQ can cut how loud
   *  the peak sounds, but not how long the room mode itself rings once
   *  excited. `null` only for a degenerate candidate (every bin
   *  floored).
   *
   *  NOTE: this was `2.2·Q/(π·f)` and shipped π times too short. The 2.2
   *  already contains the 2π (T60 = 6·ln10·Q/ω0 = 2.199·Q/f), so the
   *  extra π made it a T19 mislabelled as a T60.
   *  `packages/charts/src/modalRingingFingerprint.ts` still carries the
   *  same uncorrected π — see that file. */
  worst_peak?: { freq_hz: number; db: number; q: number; ring_s: number } | null;
  /** This candidate's worst frequency-response dip/cancellation, same
   *  smoothing and same radiated-weighted selection as `worst_peak`. A
   *  cancellation can't be refilled by the treatment step's absorption —
   *  moving the seat or speakers is the actual fix. `null` only for a
   *  degenerate candidate. */
  worst_dip?: { freq_hz: number; db: number } | null;
  /** How many acoustically equivalent placements this candidate stands for,
   *  including itself. A box room generates mirror-image seats that sound
   *  identical; they are merged before the solver's candidate cap so the cap
   *  is spent on genuinely distinct placements instead. 1 means nothing was
   *  merged into this one.
   *
   *  Backend-computed but not yet surfaced anywhere. It exists so that
   *  showing the user "a mirror of this placement" or "3 equivalent
   *  placements collapsed" is a frontend change rather than a solver one —
   *  merging is required for the search-space benefit regardless of how it
   *  is presented. */
  equivalent_count?: number;
  /** Where the merged twins were, so a future UI could offer them. Length is
   *  `equivalent_count - 1`. */
  equivalent_placements?: { listener_pos: number[]; listener_yaw: number }[];
  /** Which never-fail relaxations THIS candidate personally needed to be
   *  admitted (`OptimizeResponse.hard_filters` is the rung the whole search
   *  settled on; this is per card). Empty for a candidate the strict rung
   *  admits — i.e. for every candidate a room that fills the gallery
   *  produces. Absent on legacy payloads. */
  relaxed?: HardFilterRelaxation[];
  /** Count of distinct deep-cancellation REGIONS (contiguous runs on the
   *  smoothed curve past a JND=3 "bad" threshold), not a bin count off the
   *  800-point grid.
   *
   *  The threshold value matches the one `evalBand` uses client-side, but
   *  it is applied to a RADIATED-WEIGHTED JND here, so the two no longer
   *  classify identically: a deep null the speakers cannot excite counts
   *  as a cancellation for `evalBand` and does not count here. That is
   *  deliberate — this field drives placement copy, where reporting an
   *  inaudible cancellation as a downside is the bug this was changed to
   *  fix. Don't "restore parity" by dropping the weight. */
  deep_dip_count?: number;
  /** One entry per region `deep_dip_count` counts, ascending by
   *  frequency, each represented by its deepest bin. The count alone
   *  can say "three cancellations" but not whether they are comparable
   *  or wildly different, which the placement copy needs. Invariant:
   *  `deep_dips.length === deep_dip_count`. */
  deep_dips?: { freq_hz: number; db: number }[];
  /** Count of reflection arrivals (bounces >= 1) within the first 20 ms
   *  (the comb-filtering window — see `docs/plans/2026-08-24-placement-
   *  proscons-spec.md` §3's Family C) — placement axis C1. `null`/absent
   *  when the candidate has no raytrace (e.g. no resolvable main pair). */
  early_reflection_count?: number | null;
  /** The single loudest early reflection (same 20 ms window as C1),
   *  attributed to a wall — placement axis C2. `db` is relative to the
   *  direct sound under an explicit, documented modelling assumption (the
   *  direct-sound packet = 80 dB SPL — see `REFERENCE_DB_SPL` in
   *  `backend/soundstage_metrics.py`), not a calibrated measurement.
   *  `wall` names the facet the reflection point is closest to (best-
   *  effort — see `_nearest_facet_name`'s own doc for the one geometry
   *  case it can misattribute: near-coplanar facets). `null` when no
   *  reflection falls inside the window at all. */
  strongest_early_reflection?: {
    db: number; t_ms: number; wall: string | null; position: Vec3;
  } | null;
  /** Left/right early-energy balance (0-80 ms, ISO 3382's early-energy
   *  window, direct sound included per that convention) in the same
   *  80 dB-referenced dB scale as `strongest_early_reflection.db` —
   *  placement axis C3. The DIFFERENCE between `left_db` and `right_db`
   *  is accurate regardless of whether the 80 dB reference assumption
   *  holds (the reference cancels in a subtraction); only each side's
   *  absolute value depends on it. `null` when either side has no early
   *  energy at all. */
  side_balance?: { left_db: number; right_db: number } | null;
  /** Full `/api/admin/freq_response` payload for this candidate's layout —
   *  computed once after ranking, not during the search funnel. Feeds
   *  freq_response + impulse-response charts without a separate Compute. */
  chart_freq?: FreqResponseResponse | null;
  /** Full `/api/admin/rt60` payload for this candidate's layout — same
   *  timing as `chart_freq`. Feeds rt60-sub + rt60-above charts. */
  chart_rt60?: RT60Response | null;
  /** Precomputed 3D viz (pressure + reflections + velocity) for this
   *  candidate's layout — same timing as the chart registry. The gallery
   *  primes live-fetch caches from this so flipping A/B/C and switching
   *  viz modes is a cache hit, not a second solve. */
  viz?: PersistedVizPayload | null;
}
/** One relaxation the placement search's hard-filter ladder may apply
 *  (`backend/admin_optimize.py`, `HARD_FILTER_LADDER`), in ladder order.
 *  `seat_margin` is the seat-lattice fallback that precedes the ladder.
 *  Never relaxed, so never a label here: a speaker outside the room, a
 *  cabinet's own footprint through a wall, a seat inside furniture, a wall in
 *  the seat-to-speaker sightline. */
export type HardFilterRelaxation =
  | 'seat_margin'
  | 'surround_clearance'
  | 'speaker_clearance'
  | 'speaker_distance'
  /** A centre / surround / rear channel sits closer to the seat than the
   *  mains, along its own ITU azimuth, because the full circle did not fit
   *  the room; an overhead channel was steepened. The mains never move and
   *  the layout is never downgraded. The receiver's per-channel distance
   *  setting compensates. */
  | 'channel_distance'
  | 'sightline_furniture'
  | 'off_limits';

/** Which rung of the never-fail hard-filter ladder a placement search had
 *  to relax to, and how many candidates every rung it evaluated left. `rung`
 *  `'strict'` with an empty `relaxed` means the pre-ladder search ran
 *  unchanged. Nothing renders this yet; it rides along so the gallery can
 *  say "speakers sit closer to the wall than usual" without a second solve. */
export interface HardFilterReport {
  rung: string | null;
  relaxed: HardFilterRelaxation[];
  /** Survivors needed before the ladder stops descending: 8 for a free seat
   *  sweep, 3 (one full gallery) for a pinned or anchored seat. */
  k_min: number;
  /** Names of the rungs that were evaluated, in order; the ladder stops at
   *  the first one that fills `k_min`. */
  rungs: string[];
  survivors_per_rung: number[];
  warning?: string | null;
}

export interface OptimizeResponse {
  top5: OptimizeCandidate[];
  score_grid?: number[][];
  valid_grid?: boolean[][];
  mode?: 'line_1d' | '2d_floor';
  line_path?: [number, number][] | null;
  /** The most consequential entry of `warnings` — kept for callers that read
   *  one string. */
  warning?: string | null;
  /** Every relaxation and caveat the search applied, most consequential
   *  first: a hard-filter relaxation ("placement relaxed: …", with
   *  off-limits zones ignored the loudest), then a livability relaxation
   *  ("livability relaxed: …"), then the hard-to-symmetrise note. */
  warnings?: string[];
  /** Null on an empty (no-candidate) result. */
  hard_filters?: HardFilterReport | null;
  /** Room-level RT60 computed once per placement solve — same `rt60_fine`
   *  curve `/api/admin/rt60` plots. Gallery preview nulls live
   * `analysis.rt60`, so the UI reads this instead. */
  room_rt60?: RT60Response;
}

/** Optimize candidate with optional precomputed visualization payload. */
export interface PlacementArtifactCandidate extends OptimizeCandidate {
  viz?: PersistedVizPayload | null;
}

/** Versioned placement artifact bundled in onboarding template snapshots. */
export interface PlacementArtifact extends Omit<OptimizeResponse, 'top5'> {
  version: 1;
  top5: PlacementArtifactCandidate[];
}

export function isPlacementArtifact(value: unknown): value is PlacementArtifact {
  if (typeof value !== 'object' || value === null) return false;
  const rec = value as PlacementArtifact;
  return rec.version === 1 && Array.isArray(rec.top5)
    && rec.top5.length === PLACEMENT_GALLERY_LIMIT;
}
export interface OptimizeListenerRequest extends SceneBase {
  fmax?: number;
  main_l_pos?: Vec3 | null;
  main_r_pos?: Vec3 | null;
  exclusion_zones?: [number, number, number, number][] | null;
  min_listener_speaker_dist_m?: number | null;
  /** When set, the listener-facing yaw is pinned to this value and the
   *  solver evaluates only this facing instead of sweeping candidates. */
  listener_yaw_rad?: number | null;
  /** Phase-A: when present, backend dispatches to `search_layout`
   *  (fixed channel angles, equidistant head-level, footprint-validated)
   *  instead of the legacy axis-constrained search. */
  layout?: SpeakerLayout;
  furnishing?: Furnishing | null;
}
export interface OptimizeSpeakersRequest extends SceneBase {
  listener_yaw_rad?: number | null;
  fmax?: number;
  main_l_idx?: number | null;
  main_r_idx?: number | null;
  enforce_equilateral?: boolean;
  min_speaker_distance_m?: number | null;
  /** Optional max listener↔speaker distance. With min, replaces the
   *  backend's room-scaled `_distance_range` heuristic in search_layout. */
  max_speaker_distance_m?: number | null;
  exclusion_zones?: [number, number, number, number][] | null;
  layout?: SpeakerLayout;
  furnishing?: Furnishing | null;
}
export type OptimizeJointRequest = OptimizeSpeakersRequest;

/** Bare-room-vs-treated comparison request for `/api/admin/room_treatment` —
 *  not the same path as the `place_treatment` JobAction below (that one
 *  runs through the async job model); this is the legacy synchronous
 *  request shape some callers still use. */
export interface RoomTreatmentRequest extends SceneBase {
  tier?: TreatmentTier;
  coverage?: Coverage;
  /** Total panel budget when coverage='custom' (1-40). Required in that
   *  case, ignored otherwise. */
  custom_count?: number | null;
  /** Caller-chosen product mix. Wins over coverage/custom_count when both
   *  are sent — see `ManualSelectionItem`. */
  manual_selection?: ManualSelectionItem[] | null;
  furnishing?: Furnishing | null;
}

/** Coverage levels within a tier — applied as `panels.slice(0, reveal_indices[level])`.
 *  This is only "the most impactful subset" if the backend solver keeps
 *  emitting panels in highest-marginal-JND-first order — a backend contract,
 *  not something this file can check. Verify against the backend/contracts
 *  before trusting the slice, not this comment. */
export type RevealLevel = 'essential' | 'balanced' | 'full';

export interface RevealIndices {
  essential: number;
  balanced: number;
  full: number;
}

/** Per-SKU rollup the greedy solver attaches to each placement run.
 *  Computed inside `greedy_solver._aggregate_summary` and surfaced
 *  verbatim through the treatment endpoint so the result-page product
 *  cards (image, price, rating, absorption sparkline) need no second
 *  backend call. */
export interface ProductSummary {
  sku: string;
  name: string;
  /** How many of this SKU the solver placed in the room. */
  quantity: number;
  unit_price_cents: number;
  total_price_cents: number;
  width_m: number;
  height_m: number;
  depth_m: number;
  /** Family root — useful when listing siblings of a placed SKU. */
  sku_prefix?: string;
  /** Path under /images/products/<folder>/<file>.png served by the BFF.
   *  Already cache-busted with ?v=N by `_resolve_image_url`. Null when
   *  no rendered image exists for this SKU. */
  image_url?: string | null;
  /** Manufacturer or Amazon affiliate URL — `#` if `_affiliate_url` had
   *  nothing to build from. */
  url?: string;
  mounting_type?: AdminPanelMountingType;
  is_diffuser?: boolean;
  /** Each placed instance's wall + (u, v). Lets the UI dot the room
   *  outline when a product card is focused. */
  positions?: { wall_id: string; u: number; v: number }[];
  /** Amazon-style 1–5 star rating, null until the catalog row populates. */
  rating?: number | null;
  review_count?: number | null;
  /** Per-octave absorption — `{ "125": 0.3, "250": 0.6, ... }`. Drives
   *  the small absorption sparkline on the product card. */
  absorption_coeffs?: Record<string, number>;
}

export interface RoomTreatmentResponse {
  panels: AdminPanel[];
  n_panels: number;
  tier: TreatmentTier;
  total_cost_usd: number;
  /** Indices into `panels[]` for the three within-tier coverage levels. */
  reveal_indices: RevealIndices;
  /** Resolved panel ceiling the solver was allowed to use (coverage's
   *  COVERAGE_PANELS count, or the tier's own max_products when no
   *  coverage was given). `n_panels` can land below this — the solver's
   *  1-JND convergence gate stops placing panels once every band is
   *  within 1 JND of its RT60 target, independent of remaining budget. */
  panel_budget?: number;
  /** Per-band RT60 before any treatment ({band_hz: seconds}). */
  rt60_before?: Record<string, number>;
  /** Per-band RT60 after placing all `panels` ({band_hz: seconds}). */
  rt60_after?: Record<string, number>;
  /** Per-band RT60 target the JND loop aimed at. */
  rt60_target?: Record<string, number>;
  /** Recommended-products rollup keyed by SKU — see `ProductSummary`. */
  product_summary?: ProductSummary[];
}

// --- async jobs (M3) -----------------------------------------------------
// The unified backend job model: every heavy editor op is `{ scene, action }`
// submitted to /api/admin/jobs, returning a job_id the client polls.

export interface Scene extends RoomDims {
  speakers: AdminSpeaker[];
  listener_pos: Vec3;
  panels: AdminPanel[];
  furniture?: AdminFurniture[];
  windows?: AdminWindow[];
  wall_material?: string | null;
  floor_material?: string | null;
  furnishing?: Furnishing | null;
  materials?: SceneBase['materials'];
  // Geometry — omitted for a plain rectangular room, present once the user
  // draws an L-shape, a custom polygon, or a non-flat ceiling.
  room_shape?: RoomShape;
  footprint_vertices?: number[][] | null;
  wall_labels?: (string | null)[] | null;
  wings?: WingSpec[];
  ceiling_profile?: CeilingProfile | null;
}

/** Fields shared by every "solve both listener + speakers" search:
 *  `solve_joint`'s free-form advanced-rail search and `solve_room`'s stage 1
 *  (identical inputs, both hit `search_layout` server-side — see
 *  docs/plans/2026-09-02-unified-room-solve-plan.md §3.2). Declared once so
 *  the two kinds cannot drift apart; `placementJobAction`
 *  (`packages/room-flow/src/placement.ts`) builds these through one shared
 *  code path and spreads the result into whichever action kind it returns. */
export interface JointPlacementFields {
  main_l_idx?: number | null;
  main_r_idx?: number | null;
  enforce_equilateral?: boolean;
  min_speaker_distance_m?: number | null;
  /** Optional max listener↔speaker distance. With min, replaces the
   *  backend's room-scaled `_distance_range` heuristic in search_layout. */
  max_speaker_distance_m?: number | null;
  exclusion_zones?: [number, number, number, number][] | null;
  /** When set, pins the listener-facing yaw (radians) so the joint
   *  search_layout sweep evaluates only this facing. */
  listener_yaw_rad?: number | null;
  layout?: SpeakerLayout;
}

/** Per-candidate livability sweep result (§4 of the unified-solve plan): the
 *  lowest relaxation tier a placement search's K_MIN=8 gate had to fall back
 *  to before it found enough usable candidates. Nothing renders this yet —
 *  see AGENTS.md's copy-minimalism rule — it rides along so a later "seat
 *  1.1 m off the back wall" pros/cons line needs no second solve. */
export interface LivabilityReport {
  /** False for any non-rectangular footprint — rear-clearance/front-band
   *  margins need a rectangle to measure against. */
  clearance_applicable: boolean;
  /** 0 (strict) … 2 (most relaxed). Always 0 when `clearance_applicable` is
   *  false. */
  tier: number;
  /** Which rules the selected tier had to relax to reach K_MIN candidates.
   *  `furniture_overlap` and `seat_wall_clearance` are the two last-resort
   *  rungs below the tier ladder (never-fail rules, §3.6): a speaker sharing
   *  floor space with furniture, and a caller-pinned seat under 0.30 m from
   *  the wall behind it. */
  relaxed: Array<
    | 'rear_clearance' | 'speaker_front_band' | 'seat_anchor'
    | 'furniture_overlap' | 'seat_wall_clearance'
  >;
  k_min: number;
  /** Candidate counts surviving at tier 0, 1, 2 — `[n0, n1, n2]`, for
   *  diagnostics and copy. */
  survivors_per_tier: number[];
  warning?: string | null;
}

/** A stage that failed inside `solve_room` without failing the whole job —
 *  see §3.5's partial-success table. `error_code`/`error_stage` are closed
 *  sets on the backend (`backend/job_errors.py`); kept as plain strings here
 *  since the TS side has no reason to enumerate them by hand. */
export interface JobFailure {
  error_code: string;
  error_stage: string;
}

/** The unified onboarding solve: best placement (stage 1, identical inputs
 *  to `solve_joint` — see `JointPlacementFields`), ten panels (stage 2),
 *  then the treated room's freq/RT60/soundstage (stage 3). Backend:
 *  `SolveRoomAction` in `app.py`, joins the `JobAction` union. Submitted
 *  through the onboarding freebie door (`submitOnboardingSolve`) or, once
 *  that is spent, the metered `/api/admin/jobs` door via `submitJob` — see
 *  docs/plans/2026-09-02-unified-room-solve-plan.md §3.2-3.6. */
export interface SolveRoomAction extends JointPlacementFields {
  kind: 'solve_room';
  /** Stage 2 panel budget. No `'custom'` — the unified solve always uses a
   *  tier's own product mix, never a caller-chosen count. */
  coverage?: Exclude<Coverage, 'custom'>;
  /** Stage 2 treatment tier to place. */
  tier?: TreatmentTier;
  /** Stage 3: evaluate the treated scene (freq + rt60 + soundstage) before
   *  returning. `false` stops after stage 2 with `analysis: null` — the
   *  editor's own auto-sim (`BUMP_ANALYSIS`) fills the charts on landing
   *  instead (§3.5's stage-3 partial-success row). */
  evaluate?: boolean;
}

/** `solve_room`'s response — everything the unified solve produced in one
 *  round: the full ranked placement list (`pick_index` names which one was
 *  applied, always 0 today but explicit on purpose), the livability sweep
 *  that picked it, and stage 2/3 results, each independently nullable on
 *  partial success (§3.5). `analysis` mirrors `AdminEvaluateResponse` minus
 *  `eval_remaining` — this job never spends a pooled evaluation itself (see
 *  §3.6), so there is nothing to echo back. */
export interface SolveRoomResponse {
  placement: OptimizeResponse;
  pick_index: number;
  livability: LivabilityReport;
  treatment: RoomTreatmentResponse | null;
  treatment_error: JobFailure | null;
  analysis: Omit<AdminEvaluateResponse, 'eval_remaining'> | null;
  analysis_error: JobFailure | null;
  warnings: string[];
}

export type JobAction =
  // `coverage` sets the panel budget (minimum=6 / medium=10 / maximum=16,
  // or 'custom' + `custom_count` for a caller-chosen total, 1-40). Omit it
  // to fall back to the tier's own max_products — the tier-comparison view
  // (OptimizePanel) does this; the onboarding + placement flows send it.
  // `manual_selection` is a separate, opt-in path: a fixed caller-chosen
  // product multiset the solver only positions (does not select) — it wins
  // over coverage/custom_count on the wire when both are sent.
  | {
      kind: 'place_treatment';
      tier: TreatmentTier;
      coverage?: Coverage;
      custom_count?: number;
      manual_selection?: ManualSelectionItem[];
    }
  | {
      // Bare-room eigensolve pre-warm.  Geometry-only (the Scene's
      // speakers/listener/panels are ignored by the backend handler).
      // Runs in a spawn subprocess so the FastAPI event loop stays
      // responsive even on long MUMPS factorisations.  Lands the result
      // in the on-disk voxel cache; downstream consumers for the same
      // geometry hit the cache without re-solving.
      //
      // No `fmax` — the backend uses its canonical
      // `voxel_modal.BARE_EIGENSOLVE_FMAX_HZ` so every caller shares
      // one cache entry per geometry.
      kind: 'modal_warm';
    }
  | {
      kind: 'solve_listener';
      fmax?: number;
      main_l_pos?: Vec3 | null;
      main_r_pos?: Vec3 | null;
      exclusion_zones?: [number, number, number, number][] | null;
      min_listener_speaker_dist_m?: number | null;
      /** When set, the listener-facing yaw is pinned to this value and the
       *  solver evaluates only this facing instead of sweeping candidates. */
      listener_yaw_rad?: number | null;
      /** Phase-A: when present, backend dispatches to `search_layout`
       *  (fixed channel angles, equidistant head-level, footprint-validated)
       *  instead of the legacy axis-constrained search. */
      layout?: SpeakerLayout;
    }
  | {
      kind: 'solve_speakers';
      fmax?: number;
      listener_yaw_rad?: number;
      main_l_idx?: number | null;
      main_r_idx?: number | null;
      enforce_equilateral?: boolean;
      min_speaker_distance_m?: number | null;
      /** Optional max listener↔speaker distance. With min, replaces the
       *  backend's room-scaled `_distance_range` heuristic in search_layout. */
      max_speaker_distance_m?: number | null;
      exclusion_zones?: [number, number, number, number][] | null;
      layout?: SpeakerLayout;
    }
  | ({
      kind: 'solve_joint';
      fmax?: number;
    } & JointPlacementFields)
  | SolveRoomAction
  | {
      /** Heavy editor physics routed through the durable resource queue. */
      kind:
        | 'modal'
        | 'modal_volume'
        | 'modal_volume_sweep'
        | 'modal_velocity'
        | 'freq_response'
        | 'rt60'
        | 'raytrace'
        | 'soundstage'
        | 'speaker_position_sweep'
        | 'panel_absorption'
        | 'evaluate';
      [key: string]: unknown;
    }
  | ({ kind: 'analyze_scene' } & AnalyzeSceneRequest);

/** Metered composite: one logical "Compute" click runs freq + RT60 + soundstage. */
export interface AdminEvaluateRequest {
  request_id: string;
  freq_response: FreqResponseRequest;
  rt60: RT60Request;
  soundstage: SoundstageRequest;
}

export interface AdminEvaluateResponse {
  freq: FreqResponseResponse;
  rt60: RT60Response;
  soundstage: SoundstageResponse | null;
  /** null when the user has an unlimited subscription. */
  eval_remaining: number | null;
}

/** Job pipeline W2 (2026-09-06), §3.5 "compute-everything": ONE job kind
 *  that replaces the guest three-job fan-out AND the standalone per-layer
 *  viz calls `useModalViz` used to submit for the "after" (current-scene)
 *  slot. Carries the same fields as `AdminEvaluateRequest` plus the sweep
 *  params for the three heavy viz layers — defaults mirror what the client
 *  already sent piecemeal (`VOL_SWEEP` in `web/src/lib/volumetricCache.ts`,
 *  the raytrace/velocity request params in `useModalViz`), so the backend
 *  is free to default any field the caller omits. */
export interface AnalyzeSceneRequest {
  request_id: string;
  freq_response: FreqResponseRequest;
  rt60: RT60Request;
  soundstage: SoundstageRequest;
  viz: {
    volume: { f_min: number; f_max: number; df: number };
    raytrace: { n_rays: number };
    velocity: { f_min: number; f_max: number };
  };
}

/** Response for `analyze_scene`. `freq`/`rt60`/`soundstage` come back inline
 *  exactly like `AdminEvaluateResponse` (charts read them the same way);
 *  `modal`/`volume`/`velocity`/`raytrace` are ALL artifact references now
 *  (job pipeline W5) — `modal`'s surfaces via `ModalRefResponse`,
 *  `raytrace`'s five parallel arrays via `RaytraceRefResponse`, `volume`/
 *  `velocity` via `VolumeSweepRefResponse`/`VelocityRefResponse` — see
 *  `ArtifactRef`. The inline forms are typed alongside them because the
 *  standalone `/api/admin/modal`/`/api/admin/raytrace` endpoints, landing
 *  bundles, and v1 `viz_json` still produce them; `isModalRef`/
 *  `isRaytraceRef` discriminate, and `resolveModal`/`resolveRaytrace`
 *  (`@roomtreatment/api-client`) hydrate either into the inline shape. Any
 *  of the four viz fields may be `null` if that layer's solve failed —
 *  `warnings` carries why; a `null` layer never fails the whole job. */
export interface AnalyzeSceneResponse {
  freq: FreqResponseResponse;
  rt60: RT60Response;
  soundstage: SoundstageResponse;
  modal: ModalResponse | ModalRefResponse | null;
  volume: VolumeSweepRefResponse | null;
  velocity: VelocityRefResponse | null;
  raytrace: RaytraceResponse | RaytraceRefResponse | null;
  warnings: string[];
  /** Owner's remaining pooled evaluations after this job's charge. Not part
   *  of the worker result: the done poll carries it and `runQueued` merges
   *  it in (see createAdminApi.ts), exactly like `evaluate`'s. */
  eval_remaining?: number | null;
}

/** Job pipeline W3 (2026-09-06), §3.1 "origin contract": who asked, from
 *  where, for what document state. Sent on `POST /api/admin/jobs`, echoed
 *  verbatim on every poll (`JobPollResponse`) and on each row of
 *  `GET /api/admin/jobs/active` so a tab can reattach only to its own jobs
 *  and a Design can find results computed for it by another tab. All four
 *  fields are plain strings, max 128 chars server-side.
 *
 *  - `clientRequestId`: minted per submit (`crypto.randomUUID()`); a
 *    caller retrying the SAME logical submit (`submitWithRetry`) reuses it
 *    — the server treats `(owner_key, clientRequestId)` as one job, which
 *    is the whole idempotency guarantee, checked before the content-hash
 *    dedup.
 *  - `tabId`: one per browser tab, `sessionStorage` (`web/src/lib/tabId.ts`)
 *    — unlike `anonId`, which is per-browser.
 *  - `designId`: the `DesignRecord.id` the request was built from
 *    (`iterations.activeId` at submit time).
 *  - `docHash`: `docHash(doc)` (`editorState.ts`) at submit time — the
 *    client-side staleness check on apply: a result whose `docHash` no
 *    longer matches the target Design's current doc is parked
 *    (`pendingResult`) instead of applied. See `APPLY_ROOM_SOLVE` /
 *    `COMMIT_PLACEMENT_SIM`'s origin handling in `editorState.ts`. */
export interface JobOrigin {
  clientRequestId: string;
  tabId: string;
  designId: string;
  docHash: string;
  /** The product surface that submitted the job. Absent = the editor.
   *  `'simulator'` = the free Room Acoustics Simulator (plan S23): the
   *  backend admits it through `simulator_limits` (guest 1 run then 401
   *  `signup_required`, signed in 20/hour then 429) instead of the eval
   *  pool, and only for `analyze_scene` on a room with no panels or
   *  furniture. Not stored on the job row or echoed on polls. */
  source?: 'simulator';
}

export interface JobSubmitRequest {
  scene: Scene;
  action: JobAction;
  origin?: JobOrigin;
}
export interface JobSubmitResponse {
  job_id: string;
}
/** Server-measured latency split for one job, from `backend/routers/jobs.py`'s
 *  `_job_timings`. Both halves are differences between timestamps the DATABASE
 *  wrote with NOW(), so they carry no client clock skew and no poll
 *  granularity — which is the point:
 *
 *  - `queued_ms` — everything before the physics: admission, the worker's
 *    claim, then the spawn + `app` import + DB-pool mint of the fresh solve
 *    child (`jobs.started_at` is overwritten from inside that child once it
 *    begins the job body).
 *  - `run_ms` — the job body itself, up to the terminal write.
 *
 *  Read these instead of timing a solve in the browser. The client-side
 *  attempt (`useJobRun`'s old `queue_wait_ms`) recomputed itself on every
 *  non-pending poll, so what it reported on `solve_completed` was measured
 *  all the way to `done` — nearly the whole job — and production read
 *  "22 s queued" for jobs that waited under a second.
 *
 *  Either half is `null` when its timestamp is absent: a row that never ran
 *  (still pending, failed at admission) or one requeued after a crash has
 *  `started_at` NULL. Optional on the type because a backend older than this
 *  contract omits the field entirely. */
export interface JobTimings {
  queued_ms: number | null;
  run_ms: number | null;
}

export type JobPollResponse =
  | {
      status: 'pending' | 'running';
      timings?: JobTimings;
      resume_count?: number;
      queue_position?: number;
      eta_seconds?: number;
      resource_class?: 'small' | 'medium' | 'large';
      estimated_ram_bytes?: number;
      /** Which stage of a multi-stage job (currently only `solve_room`) is
       *  running right now — reported only while `status === 'running'`.
       *  Closed set on the backend (`jobs.stage`, §3.4 of the unified-solve
       *  plan). `SolveLoader` uses it to pick a phase list
       *  (`PLACEMENT_SOLVE_PHASES` / `TREATMENT_SOLVE_PHASES` /
       *  `ANALYSIS_SOLVE_PHASES`, `packages/room-flow/src/solvePhases.ts`). */
      stage?: 'placement' | 'treatment' | 'analysis' | null;
      origin?: JobOrigin | null;
    }
  | {
      status: 'done';
      result: unknown;
      eval_remaining?: number | null;
      origin?: JobOrigin | null;
      timings?: JobTimings;
    }
  | {
      status: 'error';
      error: string;
      error_code?: string;
      error_stage?: string;
      origin?: JobOrigin | null;
      timings?: JobTimings;
    };

/** Mirrors backend/routers/jobs.py's `AdminJobAppliedResponse` —
 *  `POST /api/admin/jobs/{id}/applied`'s response (§3.11 step 5 of
 *  docs/plans/2026-09-02-unified-room-solve-plan.md). Always 200; `applied`
 *  is the entire race arbiter — `true` means THIS caller won the atomic
 *  claim and may dispatch `APPLY_ROOM_SOLVE`, `false` means someone else
 *  already applied the job (or the caller doesn't own it), and the caller
 *  should reload the room instead. */
export interface JobAppliedResponse {
  applied: boolean;
}

/** `GET /api/scene/{token}`'s `pending_job` field (§3.11 step 4) — the
 *  newest `solve_room` job pointed at this room that no tab has applied yet
 *  (backend/db.py's `get_pending_room_job`), or `null` when there is
 *  nothing to reconcile. `kind` is always `'solve_room'` today (the only
 *  kind `jobs.room_token` is ever set for) but typed loosely rather than as
 *  a literal, matching how the backend itself treats it as an open string.
 *  `RoomShell` is the only current reader: `done` → fetch the result, claim
 *  it, and apply; `pending`/`running` → reconnect the generate step;
 *  `error` → nothing (no result to apply). */
export interface PendingRoomJob {
  id: string;
  kind: string;
  status: 'pending' | 'running' | 'done' | 'error';
}

// --- materials & products -----------------------------------------------
export interface MaterialOption {
  key: string;
  label: string;
  alpha: number[];
}
export interface MaterialsResponse {
  octave_bands: number[];
  floor: { default: string; options: MaterialOption[] };
  wall: { default: string; options: MaterialOption[] };
}

/** Catalog product category — API may add values beyond this union. */
export type ProductCategory =
  | 'absorption'
  | 'bass_trap'
  | 'diffuser'
  | 'hardware'
  | string;

/** Where a panel product is meant to be mounted. */
export type ProductMountingType =
  | 'wall'
  | 'ceiling'
  | 'floor'
  | 'floor_standing'
  | 'freestanding'
  | 'corner'
  | string;

/**
 * Admin catalog product — fields align with FastAPI `/api/admin/products` seeds.
 * Dimensions may be metres (`width_m`) or centimetres (`width_cm`).
 */
export interface AdminProduct {
  sku: string;
  name: string;
  description?: string;
  category?: ProductCategory | null;
  tier?: string;
  width_cm?: number;
  height_cm?: number;
  depth_cm?: number;
  width_m?: number;
  height_m?: number;
  depth_m?: number;
  mounting_type?: ProductMountingType;
  /** 1 = active, 0 = hidden from catalog filters */
  active?: number;
  image_url?: string;
  price_cents?: number;
  unit?: string;
  pack_qty?: number;
  coverage_m2?: number;
  is_diy?: number;
  colours?: string;
  material?: string;
  asin?: string;
  search_terms?: string;
  absorption_coeffs?: Record<string, number> | number[];
}

// --- template snapshots (GET /api/templates, /{id}, /{id}/{variant}) --
// Public, read-only. Two variants per room: `bare` (panels stripped) and
// `treated` (seed doc as designed, panels included). Wizard hydrates
// `wizardVariant` from the seed JSON (currently `bare`) — flip that field,
// not this union. See docs/templates.md.
// `doc` on TemplateDetail is a full EditorDoc, imported with `import type`
// from editorState.ts (circular type-only; erased at compile time).

export const TEMPLATE_VARIANTS = ['bare', 'treated'] as const;
export type TemplateVariant = (typeof TEMPLATE_VARIANTS)[number];

export interface TemplateVariantStatus {
  hash: string;
  ready: boolean;
}

/** `GET /api/templates` list item. `hash`/`ready` alias
 *  `variants[defaultVariant]` (the wizard default). Pick explicitly
 *  (`variants.treated`) for landing-page / compare slots. */
export interface TemplateListItem {
  id: string;
  name: string;
  blurb: string;
  dims: { Lx: number; Ly: number; H: number };
  variants: Record<TemplateVariant, TemplateVariantStatus>;
  defaultVariant: TemplateVariant;
  hash: string;
  ready: boolean;
}

/** Same shape `TREATMENT_TIER_DONE`'s `result` expects (editorState.ts's
 *  `TierResult`) — kept as an independent copy rather than importing
 *  `TierResult` itself so this file's only editorState dependency stays the
 *  `EditorDoc`/`PanelEntry` type-only import below. */
export interface TemplateTreatmentResult {
  tier: TreatmentTier;
  panels: PanelEntry[];
  revealIndices: RevealIndices;
  cost: number;
  rt60Before: Record<string, number>;
  rt60After: Record<string, number>;
  rt60Target: Record<string, number>;
  productsSummary: ProductSummary[];
}

/** `results` shape a ready `TemplateDetail` carries — `freq`/`rt60`/
 *  `soundstage`/`beforeStale`/`afterStale` are exactly what `HYDRATE`'s
 *  `results` field + `analysisFromResults()` expect (extra keys, including
 *  `treatment` here, are ignored by that reader). The same `treatment`
 *  object is passed as `HYDRATE.treatment` so coverage/reveal controls
 *  have a tier result without a follow-up `TREATMENT_TIER_DONE` (that
 *  path bumps `token` and clobbers `rt60.panel_absorption`). See
 *  applyTemplateDetail.ts. */
export interface TemplateResults {
  freq: FreqResponseResponse;
  rt60: RT60Response;
  soundstage: SoundstageResponse | null;
  beforeStale: boolean;
  afterStale: boolean;
  /** Omitted for an untreated/bare template; older snapshots may still carry
   * an empty synthetic shell here for wire compatibility. */
  treatment?: TemplateTreatmentResult | null;
  /** Precomputed placement solve for onboarding template hydration. */
  placement?: PlacementArtifact | null;
}

/** `GET /api/templates/{id}/{variant}` 200 body. `viz` is a
 *  `PersistedVizPayload` (volume + raytrace before/after) when precompute
 *  succeeded; omitted when null (3D then live-fetches). 404 = `{ready: false}`. */
export interface TemplateDetail {
  id: string;
  variant: TemplateVariant;
  hash: string;
  doc: EditorDoc;
  results: TemplateResults;
  viz?: PersistedVizPayload | null;
}
